import { useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'

import { AIPlatformAPI } from 'api/index'

export interface IScenarioOption {
  label: string
  value: string
  type: string
}

type Scenario = {
  scenarioId: string
  name: string
  type: string
  exposed: boolean
  archived: boolean
}

const scenariosOptions = {
  SALES_INTENT: 'sales intent',
  SUPPORT_INTENT: 'support intent',
}

export const useExposedScenarios = () => {
  const { data: scenarios = [] } = useQuery<Scenario[]>({
    queryKey: ['exposedScenarios'],
    queryFn: async () => {
      try {
        const response = await AIPlatformAPI.get<Scenario[]>(
          'api/v1/scenarios/org/review'
        )
        return response.data
      } catch (error) {
        console.error('Error fetching scenarios:', error)
        throw error
      }
    },
    staleTime: 60000,
  })

  const exposedScenarioOptions: IScenarioOption[] = useMemo(() => {
    const isSalesOrSupport = (name: string) => {
      const lower = name.toLowerCase()
      if (lower === scenariosOptions.SALES_INTENT) return 1
      if (lower === scenariosOptions.SUPPORT_INTENT) return 2
      return 3
    }

    return scenarios
      .filter((scenario) => scenario.exposed)
      .sort((a, b) => {
        // Sort by keyword group first
        const aGroup = isSalesOrSupport(a.name)
        const bGroup = isSalesOrSupport(b.name)
        if (aGroup !== bGroup) return aGroup - bGroup
        // Then non-archived before archived
        if (!a.archived && b.archived) return -1
        if (a.archived && !b.archived) return 1
        // Then alphabetically
        return a.name.localeCompare(b.name)
      })
      .map((scenario) => ({
        label: scenario.name,
        value: String(scenario.scenarioId),
        type: scenario.type,
      }))
  }, [scenarios])

  return exposedScenarioOptions
}

export default useExposedScenarios
